/**
 * CacheLogger - Cache Operations (v2 Taxonomy)
 *
 * Taxonomy: cache.{operation}[.{flavor}]
 *
 * Operations:
 * - init: Cache initialization
 * - page: Page cache operations
 * - paragraph: Paragraph cache operations
 * - orchestrator: L1/L2 cache orchestration
 *
 * Flavors:
 * - .hit: Cache hit
 * - .miss: Cache miss
 * - .store: Store operation
 * - .evict: Eviction
 * - .start/.complete/.fail: Lifecycle
 *
 * @example
 * CacheLogger.page.hit(5, 'l1', { size: 1024 });
 * CacheLogger.page.miss(5);
 * CacheLogger.orchestrator.l1Hit(5, { latencyMs: 2 });
 */

import { WeBelieveLogger, captureCallerSource } from '../WeBelieveLogger';

export const CacheLogger = {
  // ============================================================================
  // Cache Initialization
  // ============================================================================
  init: {
    /**
     * Log cache initialization started
     */
    start: (cacheType: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.init.start', `${cacheType} initialization started`, source, { cacheType, ...data });
    },

    /**
     * Log cache initialization completed
     */
    complete: (cacheType: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'cache.init.complete', `${cacheType} initialized`, source, { cacheType, ...data });
    },

    /**
     * Log cache initialization failed
     */
    fail: (cacheType: string, error: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'cache.init.fail', `${cacheType} init failed: ${error}`, source, { cacheType, error, ...data });
    },
  },

  // ============================================================================
  // Page Cache
  // ============================================================================
  page: {
    /**
     * Log page cache hit
     */
    hit: (pageIndex: number, layer: 'l1' | 'l2', data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.page.hit', `Page ${pageIndex} hit (${layer})`, source, { pageIndex, layer, ...data });
    },

    /**
     * Log page cache miss
     */
    miss: (pageIndex: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'cache.page.miss', `Page ${pageIndex} miss`, source, { pageIndex, ...data });
    },

    /**
     * Log page stored in cache
     */
    store: (pageIndex: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.page.store', `Stored page ${pageIndex}`, source, { pageIndex, ...data });
    },

    /**
     * Log batch page store
     */
    batchStore: (count: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.page.batch-store', `Batch stored ${count} pages`, source, { count, ...data });
    },

    /**
     * Log page evicted from cache
     */
    evict: (pageIndex: number, reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.page.evict', `Page ${pageIndex} evicted: ${reason}`, source, { pageIndex, reason, ...data });
    },
  },

  // ============================================================================
  // Paragraph Cache
  // ============================================================================
  paragraph: {
    /**
     * Log paragraph cache hit
     */
    hit: (paragraphId: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.paragraph.hit', `Paragraph cache hit: ${paragraphId.slice(0, 8)}`, source, { paragraphId: paragraphId.slice(0, 8), ...data });
    },

    /**
     * Log paragraph cache miss
     */
    miss: (paragraphId: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'cache.paragraph.miss', `Paragraph cache miss: ${paragraphId.slice(0, 8)}`, source, { paragraphId: paragraphId.slice(0, 8), ...data });
    },

    /**
     * Log paragraphs invalidated
     */
    invalidate: (paragraphIds: string[], reason: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'cache.paragraph.invalidate', `Invalidated ${paragraphIds.length} paragraphs: ${reason}`, source, {
        paragraphIds: paragraphIds.map(id => id.slice(0, 8)),
        reason,
        ...data
      });
    },
  },

  // ============================================================================
  // Cache Orchestrator (L1/L2 Layers)
  // ============================================================================
  orchestrator: {
    /**
     * Log L1 (memory) cache hit
     */
    l1Hit: (pageIndex: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.orchestrator.l1-hit', `L1 hit: page ${pageIndex}`, source, { pageIndex, layer: 'l1', ...data });
    },

    /**
     * Log L2 (IndexedDB) cache hit
     */
    l2Hit: (pageIndex: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.orchestrator.l2-hit', `L2 hit: page ${pageIndex}`, source, { pageIndex, layer: 'l2', ...data });
    },

    /**
     * Log cache miss (both layers)
     */
    miss: (pageIndex: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'cache.orchestrator.miss', `Cache miss: page ${pageIndex}`, source, { pageIndex, ...data });
    },

    /**
     * Log batch lookup performance
     */
    batchLookup: (l1Hits: number, l2Hits: number, misses: number, latencyMs: number) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('cache.orchestrator.batch-lookup', 'Batch cache lookup', latencyMs, source, { l1Hits, l2Hits, misses });
    },

    /**
     * Log promotion from L2 to L1
     */
    promote: (pageIndex: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.orchestrator.promote', `Promoted to L1: page ${pageIndex}`, source, { pageIndex, ...data });
    },

    /**
     * Log demotion from L1 to L2
     */
    demote: (count: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.orchestrator.demote', `Demoted ${count} entries from L1`, source, { count, ...data });
    },

    /**
     * Log LRU eviction
     */
    evictLru: (count: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'cache.orchestrator.evict-lru', `Evicted ${count} L1 entries (LRU)`, source, { count, ...data });
    },

    /**
     * Log LRU eviction (legacy alias with uppercase)
     */
    evictLRU: (count: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'cache.orchestrator.evict-lru', `Evicted ${count} L1 entries (LRU)`, source, { count, ...data });
    },

    /**
     * Log content hash changed (triggers cache invalidation)
     */
    contentHashChanged: (entriesCleared: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'cache.orchestrator.content-hash-changed', `Content hash changed, cleared ${entriesCleared} L1 entries`, source, { entriesCleared, ...data });
    },
  },

  // ============================================================================
  // Content Hash Operations
  // ============================================================================
  contentHash: {
    /**
     * Log content hash changed (cache invalidation trigger)
     */
    change: (entriesCleared: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'cache.content-hash.change', `Content hash changed, cleared ${entriesCleared} L1 entries`, source, { entriesCleared, ...data });
    },
  },

  // ============================================================================
  // Cache Statistics
  // ============================================================================
  stats: (stats: { size: number; entries: number; hitRate?: number }) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('debug', 'cache.stats', 'Cache statistics', source, stats);
  },

  // ============================================================================
  // Cache Clear
  // ============================================================================
  clear: (reason: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('info', 'cache.clear', `Cache cleared: ${reason}`, source, { reason, ...data });
  },

  // ============================================================================
  // Cache Cleanup
  // ============================================================================
  cleanup: {
    /**
     * Log cache cleanup started
     */
    start: (cacheType: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.cleanup.start', `${cacheType} cleanup started`, source, { cacheType, ...data });
    },

    /**
     * Log cache cleanup completed
     */
    complete: (cacheType: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.cleanup.complete', `${cacheType} cleanup completed`, source, { cacheType, ...data });
    },

    /**
     * Log cache cleanup failed
     */
    fail: (cacheType: string, error: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'cache.cleanup.fail', `${cacheType} cleanup failed: ${error}`, source, { cacheType, error, ...data });
    },

    /**
     * Log cache cleanup removed expired/overflow entries
     * Component: cache.cleanup.removed
     */
    removed: (cacheType: string, expired: number, overflow: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'cache.cleanup.removed', `${cacheType} cleanup: ${expired} expired, ${overflow} overflow removed`, source, { cacheType, expired, overflow, ...data });
    },
  },

  // ============================================================================
  // Cache Write Operations
  // ============================================================================
  write: {
    /**
     * Log cache write failed
     */
    fail: (cacheType: string, error: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'cache.write.fail', `${cacheType} write failed: ${error}`, source, { cacheType, error, ...data });
    },
  },

  // ============================================================================
  // Stale Detection
  // ============================================================================
  stale: {
    /**
     * Log stale cache entries detected
     */
    detected: (count: number, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'cache.stale.detected', `Detected ${count} stale entries`, source, { count, ...data });
    },
  },

  // ============================================================================
  // Legacy/Compatibility Methods
  // These maintain backward compatibility during migration
  // ============================================================================

  /**
   * @deprecated Use cache.page.hit or cache.paragraph.hit
   */
  hit: (key: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('debug', 'cache.hit', `Cache hit: ${key}`, source, { key, ...data });
  },

  /**
   * @deprecated Use cache.page.miss or cache.paragraph.miss
   */
  miss: (key: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('info', 'cache.miss', `Cache miss: ${key}`, source, { key, ...data });
  },

  /**
   * @deprecated Use cache.page.evict or orchestrator.evictLru
   */
  evict: (count: number, reason: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('info', 'cache.evict', `Evicted ${count} entries (${reason})`, source, { count, reason, ...data });
  },

  /**
   * @deprecated Use cache.stale.detected
   */
  staleEntries: (count: number, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('warn', 'cache.stale.detected', `Detected ${count} stale entries`, source, { count, ...data });
  },

  /**
   * @deprecated Use cache.init.complete
   */
  initLegacy: (cacheType: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('info', 'cache.init.complete', `${cacheType} initialized`, source, { cacheType, ...data });
  },

  /**
   * @deprecated Use cache.init.fail
   */
  initFailed: (cacheType: string, error: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('error', 'cache.init.fail', `${cacheType} init failed: ${error}`, source, { cacheType, error, ...data });
  },

  /**
   * @deprecated Use cache.write.fail
   */
  writeFailed: (cacheType: string, error: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('warn', 'cache.write.fail', `${cacheType} write failed: ${error}`, source, { cacheType, error, ...data });
  },

  /**
   * @deprecated Use cache.cleanup.complete
   */
  cleanupLegacy: (cacheType: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('debug', 'cache.cleanup.complete', `${cacheType} cleanup`, source, { cacheType, ...data });
  },

  /**
   * @deprecated Use cache.cleanup.fail
   */
  cleanupFailed: (cacheType: string, error: string, data?: Record<string, unknown>) => {
    const source = captureCallerSource();
    WeBelieveLogger.logWithSource('warn', 'cache.cleanup.fail', `${cacheType} cleanup failed: ${error}`, source, { cacheType, error, ...data });
  },
};
